"use client"
import { City, FilterOptions, Project, RecursiveTranslations } from "@/data/types"
import { ProjectCard } from "./ProjectCard"
import { ProjectStatus, ProjectType, SystemLanguages } from "@/data/enums"
import Link from "next/link"
import { useEffect, useState } from "react"
import Filters from "./Filters"
import { CardContentSkeleton } from "@/components/skeleton"
import { fetchCities, fetchProjects } from "@/utils/api"
import { h4Style, h5Style } from "@/data/styles"
import clsx from "clsx"


interface Props {
  translations: RecursiveTranslations
  lang?: SystemLanguages
}

export default function Projects({ translations, lang }: Props) {
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [allCities, setAllCities] = useState<string[]>([])
  const [filters, setFilters] = useState<FilterOptions>({ city: "", propertyType: "", status: ''  })

  const allPropertyTypes = Object.values(ProjectType);
  const allStatuses = Object.values(ProjectStatus);

  useEffect(() => {
    fetchCities()
      .then((data: City[]) => {
        const cityNames = data.map((city) => city.name)
        setAllCities(cityNames)
      })
      .catch((err) => console.error("Failed to fetch cities:", err))
  }, [])

  useEffect(() => {
    setLoading(true)
    fetchProjects({ ...filters })
      .then((data) => setProjects(data))
      .finally(() => setLoading(false))
  }, [filters])

  const handleFilterChange = (newFilters: typeof filters) => setFilters(newFilters)
  const hasNoData = !loading && projects.length === 0

  return (
    <div className="max-w-[1440px] mx-auto px-[20px] md:px-[50px] pb-[50px] xl:pb-[60px]">
      <Filters
        translations={translations}
        onFilterChange={handleFilterChange}
        availableCities={allCities}
        availablePropertyTypes={allPropertyTypes}
        availableStatuses={allStatuses}
        disabled={loading}
      />

      {hasNoData ? <NoDataSegment translations={translations} /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-x-[18px] gap-y-[30px] items-stretch">
          {loading ? Array.from({ length: 8 }).map((_, idx) => <CardContentSkeleton key={idx} />)
            : projects.map((project) => (
                <Link key={project.id} href={`/${lang}/projects/${project.slug}/overview`} aria-label="project-link">
                  <ProjectCard data={project} translations={translations} />
                </Link>
              ))}
        </div>
      )}
    </div>
  )
}


const NoDataSegment = ({ translations }: Props) => {
  return (
    <div className="flex flex-col items-center justify-center text-center py-5">
      <p className={clsx(h4Style, "text-gray-600")}>
        {translations?.noResultsTitle as string}
      </p>
      <p className={clsx(h5Style, "text-gray-500")}>
        {translations?.noResultsDescription as string}
      </p>
    </div>
  )
}


